import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useChannelRoute } from '../../../pages/project-settings/use-go-to-channel';
import { useSolutionVersionsRoute } from '../../../pages/project-settings/use-go-to-solution-versions';
import { closeThenNavigate } from './close-then-navigate';

/**
 * The Edit modal's two navigations — a channel pill and a solution chip — each
 * wrapped by `closeThenNavigate`: resolve the route first, and only when there
 * is one close the modal and then navigate. A pill or chip with no route
 * changes nothing, so the form's unsaved edits survive the click.
 *
 * A hook of its own, free of design-system imports, because the modal cannot
 * be rendered in a test and this wiring (which argument is `onClose`, which is
 * the navigation) was pinned by nothing (use-modal-navigation.test.tsx).
 */
export const useModalNavigation = (onClose: (() => void) | undefined) => {
  const navigate = useNavigate();
  const channelRoute = useChannelRoute();
  const solutionVersionsRoute = useSolutionVersionsRoute();
  const goToChannel = useMemo(
    () => closeThenNavigate(onClose, channelRoute, navigate),
    [onClose, channelRoute, navigate]
  );
  const goToSolutionVersions = useMemo(
    () => closeThenNavigate(onClose, solutionVersionsRoute, navigate),
    [onClose, solutionVersionsRoute, navigate]
  );
  return { goToChannel, goToSolutionVersions };
};
